/**
 * The Flows list, as markup — slice 69.
 *
 * Split from `FlowsPage` so `verify-flow-screens` can render it from fixture
 * payloads without a router's data layer or a network.
 *
 * One row per flow: name, status as a `Tag`, the trigger in a sentence (the
 * API's `describeTrigger` with segment names resolved), which version is live
 * and whether a draft sits beside it ("Editing draft v3 · v2 is live"), and
 * the three counts the contract computes on read — active, finished,
 * converted. A flow nobody has entered shows 0 people, because the API
 * counted enrolments and found none: that zero is measured, not absent.
 *
 * The empty state offers the four starting points rather than an empty table,
 * because a person who has never built a flow does not yet know what one is.
 */
import { FLOW_STATUS_LABEL, FLOW_TEMPLATES, FLOW_TEMPLATE_ID_VALUES, type FlowSummary } from '@wend/shared';
import { Card, CardContent, Tag, cn, focusRing, type TagTone } from '@wend/ui';
import { Link } from 'react-router-dom';

const STATUS_TONE: Readonly<Record<FlowSummary['status'], TagTone>> = {
  draft: 'neutral',
  live: 'good',
  paused: 'ident-c',
  archived: 'neutral',
};

const n = (value: number) => value.toLocaleString('en-IN');

function versionLine(flow: FlowSummary): string {
  if (flow.liveVersion && flow.draftVersion) return `Editing draft v${flow.draftVersion} · v${flow.liveVersion.version} is live`;
  if (flow.liveVersion) return `Version ${flow.liveVersion.version} live since ${new Date(flow.liveVersion.launchedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}`;
  return flow.draftVersion ? `Draft v${flow.draftVersion} · never launched` : 'Never launched';
}

export function FlowStarters({ prefix = '/flows/new' }: { prefix?: string }) {
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Start from" data-role="flow-starters">
      {FLOW_TEMPLATE_ID_VALUES.map((id) => (
        <li key={id} className="min-w-0">
          <Card variant="interactive" className="relative h-full">
            <CardContent className="flex flex-col gap-1 px-4 py-3.5">
              <Link to={`${prefix}?template=${id}`} className={cn('self-start rounded-sm text-[0.95rem] font-semibold text-text after:absolute after:inset-0', focusRing)}>
                {FLOW_TEMPLATES[id].label}
              </Link>
              <p className="text-[0.84rem] leading-snug text-muted">{FLOW_TEMPLATES[id].description}</p>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}

export function FlowsContent({ flows }: { flows: readonly FlowSummary[] }) {
  if (flows.length === 0) {
    return (
      <section className="flex flex-col gap-4" data-role="flows-empty">
        <div>
          <h2 className="text-[1.05rem] font-semibold">No flows yet</h2>
          <p className="text-[0.9rem] text-muted">
            A flow is a journey people walk on their own: it starts when they enter a segment, on a schedule, when they reply or click, or when you enrol them — then waits, checks and messages them step by step. Pick a starting point; nothing launches until you press Launch.
          </p>
        </div>
        <FlowStarters />
      </section>
    );
  }

  return (
    <ul className="flex flex-col gap-2.5" aria-label="Flows" data-role="flows-list">
      {flows.map((flow) => (
        <li key={flow.id} data-flow={flow.id}>
          <Card variant="interactive" className="relative">
            <CardContent className="flex flex-col gap-2 px-4 py-3.5 md:flex-row md:items-center md:gap-6">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/flows/${flow.id}`} className={cn('min-w-0 truncate rounded-sm text-[0.98rem] font-semibold text-text after:absolute after:inset-0', focusRing)}>
                    {flow.name}
                  </Link>
                  <Tag tone={STATUS_TONE[flow.status]} data-role="flow-status">{FLOW_STATUS_LABEL[flow.status]}</Tag>
                </div>
                <p className="text-[0.86rem] text-muted">{flow.triggerSummary}</p>
                <p className="font-mono text-[0.7rem] tracking-[0.04em] text-muted">{versionLine(flow)}</p>
              </div>
              <dl className="grid grid-cols-3 gap-4 text-right md:w-[300px]" aria-live="polite">
                {[
                  { label: 'Active', value: flow.counts.active },
                  { label: 'Finished', value: flow.counts.completed },
                  { label: 'Converted', value: flow.counts.converted },
                ].map((item) => (
                  <div key={item.label} className="flex flex-col">
                    <dt className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-muted">{item.label}</dt>
                    <dd className={cn('text-[1.1rem] font-semibold tabular-nums', item.label === 'Converted' ? 'text-good' : 'text-text')}>{n(item.value)}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}
